import {
    Button,
    Form,
    FormFooter,
    FormSection,
    IconPlus,
    IconTrash,
    Inline,
    SelectField,
    Stack,
    TextAreaField,
    TextField,
    toast
} from '@7shifts/sous-chef';

import {
    FieldArray,
    FieldArrayRenderProps,
    FormikProvider,
    useFormik
} from 'formik';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { __ } from 'i18n/translate';
import TemplatePreview from '../TemplatePreview/TemplatePreview';
import FormattingToolbar from '../FormattingToolbar/FormattingToolbar';
import { useNavigate } from 'react-router-dom';
import { getSchema } from './schema';
import {
    getHeaderTypeOptions,
    getModelTypeOptions,
    getVariableTypeOptions,
    VARIABLE_FIXED_VALUE
} from './constants';
import { extractVariables, generateMetaPayload } from './domain';
import { MAX_BODY_VARIABLES /*, MAX_HEADER_VARIABLES*/ } from './schema';
import api from 'services/api';

type Props = {
    whatsappId: number;
};

export default function MessageTemplateForm({ whatsappId }: Props) {
    const HEADER_TYPE_OPTIONS = getHeaderTypeOptions();
    const MODEL_TYPE_OPTIONS = getModelTypeOptions();
    const navigate = useNavigate();
    const defaultBodyText = __('messageTemplateForm.defaultBodyText');
    const defaultFooterText = __('messageTemplateForm.defaultFooterText');
    const defaultHeaderText = __('messageTemplateForm.defaultHeaderText');
    const allowedImages: string = '.png, .jpg, .jpeg, .webp';
    const allowedVideos: string = '.mp4,.3gp';
    const allowedDocs: string = '.txt,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';
    const VARIABLE_TYPE_OPTIONS = getVariableTypeOptions();

    const formik = useFormik({
        initialValues: {
            type: MODEL_TYPE_OPTIONS[0],
            headerType: HEADER_TYPE_OPTIONS[0],
            headerContent: defaultHeaderText,
            name: '',
            bodyText: defaultBodyText,
            footerText: defaultFooterText,
            buttons: [],
            variableExamples: {} as Record<string, string>,
            variableTypes: {} as Record<string, { label: string; value: string }>,
            variableFixedValues: {} as Record<string, string>
        },
        validationSchema: getSchema(),
        onSubmit: async (values) => {
            console.log('submitting', values);
            try {
                const generatedPayload = await generateMetaPayload(values);
                console.log(generatedPayload);
                const result = await api.post(
                    '/meta/template?whatsappId=' + whatsappId,
                    generatedPayload,
                    {
                        validateStatus: () => true
                    }
                );
                if (result.status === 200) {
                    toast(__('messageTemplateForm.successMessage'), 'default');
                    setTimeout(() => {
                        navigate('/message-templates');
                    }, 2500);
                } else {
                    toast(
                        __('messageTemplateForm.errorMessage') +
                            '\n' +
                            result.data.error.error_user_msg,
                        'danger'
                    );
                }
            } catch (e) {
                console.error(e);
                toast(e as string, 'danger');
            }
            formik.setSubmitting(false);
        }
    });

    const bodyVariables = useMemo(
        () => extractVariables(formik.values.bodyText),
        [formik.values.bodyText]
    );

    // const headerVariables = useMemo(
    //     () =>
    //         formik.values.headerType.value === 'text'
    //             ? extractVariables(formik.values.headerContent)
    //             : [],
    //     [formik.values.headerContent, formik.values.headerType.value]
    // );

    useEffect(() => {
        const defaultOption = VARIABLE_TYPE_OPTIONS[0];
        bodyVariables.forEach((varNum) => {
            const key = `body_${varNum}`;
            if (!formik.values.variableTypes[key]) {
                formik.setFieldValue(`variableTypes.${key}`, defaultOption);
                formik.setFieldValue(
                    `variableExamples.${key}`,
                    defaultOption.value
                );
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bodyVariables]);

    const handleVariableTypeChange = (
        key: string,
        option: { label: string; value: string }
    ) => {
        formik.setFieldValue(`variableTypes.${key}`, option);
        if (option.value !== VARIABLE_FIXED_VALUE) {
            formik.setFieldValue(`variableExamples.${key}`, option.value);
            formik.setFieldValue(`variableFixedValues.${key}`, '');
        } else {
            const currentFixed =
                formik.values.variableFixedValues[key] || '';
            formik.setFieldValue(`variableExamples.${key}`, currentFixed);
        }
    };

    const handleFixedValueChange = (key: string, value: string) => {
        formik.setFieldValue(`variableFixedValues.${key}`, value);
        formik.setFieldValue(`variableExamples.${key}`, value);
    };

    const handleAddVariable = (field: 'bodyText' | 'headerContent') => {
        const currentText = formik.values[field];
        const existingVars = extractVariables(currentText);
        const nextVarNumber =
            existingVars.length > 0
                ? Math.max(...existingVars) + 1
                : 1;
        const varPlaceholder = `{{${nextVarNumber}}}`;
        formik.setFieldValue(field, currentText + ' ' + varPlaceholder);
    };

    const bodyTextAreaWrapperRef = useRef<HTMLDivElement>(null);

    const getBodyTextarea = useCallback((): HTMLTextAreaElement | null => {
        return bodyTextAreaWrapperRef.current?.querySelector('textarea') ?? null;
    }, []);

    const handleBodyInsert = useCallback(
        (text: string) => {
            const textarea = getBodyTextarea();
            const current = formik.values.bodyText;
            if (textarea && textarea === document.activeElement) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newText =
                    current.substring(0, start) +
                    text +
                    current.substring(end);
                formik.setFieldValue('bodyText', newText);
                requestAnimationFrame(() => {
                    const pos = start + text.length;
                    textarea.setSelectionRange(pos, pos);
                    textarea.focus();
                });
            } else {
                formik.setFieldValue('bodyText', current + text);
            }
        },
        [formik, getBodyTextarea]
    );

    const handleBodyWrap = useCallback(
        (prefix: string, suffix: string) => {
            const textarea = getBodyTextarea();
            const current = formik.values.bodyText;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selected = current.substring(start, end);
                if (selected.length > 0) {
                    const newText =
                        current.substring(0, start) +
                        prefix +
                        selected +
                        suffix +
                        current.substring(end);
                    formik.setFieldValue('bodyText', newText);
                    requestAnimationFrame(() => {
                        const newStart = start + prefix.length;
                        const newEnd = newStart + selected.length;
                        textarea.setSelectionRange(newStart, newEnd);
                        textarea.focus();
                    });
                } else {
                    const placeholder = __(
                        'messageTemplateForm.formattingPlaceholder'
                    );
                    const insertion = prefix + placeholder + suffix;
                    const newText =
                        current.substring(0, start) +
                        insertion +
                        current.substring(end);
                    formik.setFieldValue('bodyText', newText);
                    requestAnimationFrame(() => {
                        const newStart = start + prefix.length;
                        const newEnd = newStart + placeholder.length;
                        textarea.setSelectionRange(newStart, newEnd);
                        textarea.focus();
                    });
                }
            } else {
                const placeholder = __(
                    'messageTemplateForm.formattingPlaceholder'
                );
                formik.setFieldValue(
                    'bodyText',
                    current + prefix + placeholder + suffix
                );
            }
        },
        [formik, getBodyTextarea]
    );

    const handleCancel = () => {
        navigate('/message-templates');
    };

    return (
        <FormikProvider value={formik}>
            <Form formik={formik} wide={true}>
                <FormSection as="card">
                    <Inline flex={['0 1 80%', '0 1 20%']}>
                        <TextField
                            label={__('messageTemplateForm.titleModelName')}
                            name="name"
                            maxLength={512}
                            onChange={(value: string) => {
                                const formatted = value
                                    .normalize('NFD')
                                    .replace(/[\u0300-\u036f]/g, '')
                                    .replace(/\s+/g, '_')
                                    .toLowerCase();
                                formik.setFieldValue('name', formatted);
                            }}
                            value={formik.values.name}
                        />
                        <SelectField
                            label={__('messageTemplateForm.titleModelName')}
                            options={MODEL_TYPE_OPTIONS}
                            name="type"
                        />
                    </Inline>
                </FormSection>
                <Inline flex={['0 1 40%', '0 1 60%']}>
                    <Stack>
                        <FormSection
                            title={__('messageTemplateForm.headerContentForm')}
                            as="card"
                        >
                            <Stack>
                                <SelectField
                                    options={HEADER_TYPE_OPTIONS}
                                    name="headerType"
                                    onChange={(value) => {
                                        if (value.value === 'text') {
                                            formik.setFieldValue(
                                                'headerContent',
                                                __(
                                                    'messageTemplateForm.defaultHeaderText'
                                                )
                                            );
                                            return;
                                        }
                                        formik.setFieldValue(
                                            'headerContent',
                                            ''
                                        );
                                    }}
                                />
                                {formik.values.headerType.value === 'text' && (
                                    <>
                                        <TextAreaField
                                            name="headerContent"
                                            label={__(
                                                'messageTemplateForm.TextTypeOfContentForm'
                                            )}
                                            maxLength={1024}
                                        />
                                        {/* <Button
                                            theme="link-primary"
                                            onClick={() =>
                                                handleAddVariable(
                                                    'headerContent'
                                                )
                                            }
                                            disabled={
                                                headerVariables.length >=
                                                MAX_HEADER_VARIABLES
                                            }
                                        >
                                            <IconPlus size="medium" />
                                            {__(
                                                'messageTemplateForm.addVariable'
                                            )}
                                        </Button>
                                        {headerVariables.length > 0 && (
                                            <div>
                                                <p
                                                    style={{
                                                        fontWeight: 600,
                                                        marginBottom: 8
                                                    }}
                                                >
                                                    {__(
                                                        'messageTemplateForm.variableExamples'
                                                    )}
                                                </p>
                                                <Stack>
                                                    {headerVariables.map(
                                                        (varNum) => {
                                                            const key = `header_${varNum}`;
                                                            const selectedType =
                                                                formik.values
                                                                    .variableTypes[
                                                                    key
                                                                ] ||
                                                                VARIABLE_TYPE_OPTIONS[0];
                                                            return (
                                                                <div
                                                                    key={key}
                                                                >
                                                                    <SelectField
                                                                        label={`{{${varNum}}}`}
                                                                        name={`variableTypes.${key}`}
                                                                        options={
                                                                            VARIABLE_TYPE_OPTIONS
                                                                        }
                                                                        value={
                                                                            selectedType
                                                                        }
                                                                        onChange={(
                                                                            option
                                                                        ) =>
                                                                            handleVariableTypeChange(
                                                                                key,
                                                                                option
                                                                            )
                                                                        }
                                                                    />
                                                                    {selectedType.value ===
                                                                        VARIABLE_FIXED_VALUE && (
                                                                        <TextField
                                                                            label={__(
                                                                                'messageTemplateForm.varFixedValueLabel'
                                                                            )}
                                                                            name={`variableFixedValues.${key}`}
                                                                            value={
                                                                                formik
                                                                                    .values
                                                                                    .variableFixedValues[
                                                                                    key
                                                                                ] ||
                                                                                ''
                                                                            }
                                                                            onChange={(
                                                                                value: string
                                                                            ) =>
                                                                                handleFixedValueChange(
                                                                                    key,
                                                                                    value
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </Stack>
                                            </div>
                                        )} */}
                                    </>
                                )}
                                {formik.values.headerType.value !== '' &&
                                    formik.values.headerType.value !==
                                        'text' && (
                                        <>
                                            <input
                                                required={true}
                                                type="file"
                                                name="headerContentFile"
                                                accept={
                                                    formik.values.headerType
                                                        .value === 'image'
                                                        ? allowedImages
                                                        : formik.values
                                                              .headerType
                                                              .value === 'video'
                                                        ? allowedVideos
                                                        : allowedDocs
                                                }
                                                onChange={(event) => {
                                                    if (
                                                        event &&
                                                        event.target &&
                                                        event.target.files &&
                                                        event.target.files[0]
                                                    ) {
                                                        formik.setFieldValue(
                                                            'headerContent',
                                                            URL.createObjectURL(
                                                                event.target
                                                                    .files[0]
                                                            )
                                                        );

                                                        formik.setFieldValue(
                                                            'headerContentFile',
                                                            event.target
                                                                .files[0]
                                                        );
                                                    }
                                                }}
                                                className="filetype"
                                            />
                                        </>
                                    )}
                            </Stack>
                        </FormSection>
                        <FormSection
                            title={__(
                                'messageTemplateForm.titleBodyContentForm'
                            )}
                            as="card"
                        >
                            <Stack>
                                <FormattingToolbar
                                    onInsert={handleBodyInsert}
                                    onWrap={handleBodyWrap}
                                />
                                <div ref={bodyTextAreaWrapperRef}>
                                    <TextAreaField
                                        name="bodyText"
                                        maxLength={1024}
                                        minHeight={100}
                                        maxHeight={200}
                                        autoGrow={true}
                                    />
                                </div>
                                <Button
                                    theme="link-primary"
                                    onClick={() =>
                                        handleAddVariable('bodyText')
                                    }
                                    disabled={
                                        bodyVariables.length >=
                                        MAX_BODY_VARIABLES
                                    }
                                >
                                    <IconPlus size="medium" />
                                    {__(
                                        'messageTemplateForm.addVariable'
                                    )}
                                </Button>
                                {bodyVariables.length > 0 && (
                                    <div>
                                        <p
                                            style={{
                                                fontWeight: 600,
                                                marginBottom: 8
                                            }}
                                        >
                                            {__(
                                                'messageTemplateForm.variableExamples'
                                            )}
                                        </p>
                                        <Stack>
                                            {bodyVariables.map(
                                                (varNum) => {
                                                    const key = `body_${varNum}`;
                                                    const selectedType =
                                                        formik.values
                                                            .variableTypes[
                                                            key
                                                        ] ||
                                                        VARIABLE_TYPE_OPTIONS[0];
                                                    return (
                                                        <div key={key}>
                                                            <SelectField
                                                                label={`{{${varNum}}}`}
                                                                name={`variableTypes.${key}`}
                                                                options={
                                                                    VARIABLE_TYPE_OPTIONS
                                                                }
                                                                value={
                                                                    selectedType
                                                                }
                                                                onChange={(
                                                                    option
                                                                ) =>
                                                                    handleVariableTypeChange(
                                                                        key,
                                                                        option
                                                                    )
                                                                }
                                                            />
                                                            {selectedType.value ===
                                                                VARIABLE_FIXED_VALUE && (
                                                                <TextField
                                                                    label={__(
                                                                        'messageTemplateForm.varFixedValueLabel'
                                                                    )}
                                                                    name={`variableFixedValues.${key}`}
                                                                    value={
                                                                        formik
                                                                            .values
                                                                            .variableFixedValues[
                                                                            key
                                                                        ] ||
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        value: string
                                                                    ) =>
                                                                        handleFixedValueChange(
                                                                            key,
                                                                            value
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </Stack>
                                    </div>
                                )}
                            </Stack>
                        </FormSection>
                        <FormSection
                            title={__('messageTemplateForm.footerContentForm')}
                            as="card"
                        >
                            <TextField name="footerText" />
                        </FormSection>
                        <FieldArray
                            name="buttons"
                            render={({
                                remove,
                                push
                            }: FieldArrayRenderProps) => {
                                return (
                                    <FormSection
                                        title={__(
                                            'messageTemplateForm.buttonsContentForm'
                                        )}
                                        as="card"
                                    >
                                        <Stack>
                                            {formik.values.buttons.map(
                                                (_, index) => (
                                                    <Inline
                                                        key={index}
                                                        flex={[1]}
                                                        space={4}
                                                    >
                                                        <TextField
                                                            maxLength={25}
                                                            name={`buttons[${index}]`}
                                                        />
                                                        <Button
                                                            theme="link-icon"
                                                            onClick={() =>
                                                                remove(index)
                                                            }
                                                        >
                                                            <IconTrash />
                                                        </Button>
                                                    </Inline>
                                                )
                                            )}
                                        </Stack>
                                        <Button
                                            theme="link-primary"
                                            onClick={() => {
                                                push(
                                                    formik.values.buttons
                                                        .length === 0
                                                        ? __(
                                                              'messageTemplateForm.defaultButton'
                                                          )
                                                        : ''
                                                );
                                            }}
                                            disabled={
                                                formik.values.buttons.length >=
                                                3
                                            }
                                        >
                                            <IconPlus size="medium" />
                                            {__(
                                                'messageTemplateForm.buttonAddContentForm'
                                            )}
                                        </Button>
                                    </FormSection>
                                );
                            }}
                        />

                        <FormFooter
                            actions={{
                                primary: (
                                    <Button
                                        marginBottom={20}
                                        onClick={() => formik.handleSubmit()}
                                        disabled={
                                            !formik.isValid || !formik.dirty
                                        }
                                        loading={formik.isSubmitting}
                                    >
                                        {__(
                                            'messageTemplateForm.buttonSaveContentForm'
                                        )}
                                    </Button>
                                ),
                                secondary: (
                                    <Button onClick={handleCancel}>
                                        {__(
                                            'messageTemplateForm.buttonCancelContentForm'
                                        )}
                                    </Button>
                                )
                            }}
                        />
                    </Stack>
                    <TemplatePreview
                        headerContent={formik.values.headerContent}
                        headerType={formik.values.headerType.value}
                        showHeader={formik.values.headerType.value !== 'none'}
                        bodyText={formik.values.bodyText}
                        showFooter={formik.values.footerText !== ''}
                        footerText={formik.values.footerText}
                        showButtons={formik.values.buttons.length > 0}
                        buttons={formik.values.buttons}
                        variableExamples={formik.values.variableExamples}
                    />
                </Inline>
            </Form>
        </FormikProvider>
    );
}
